/**
 * Array utility functions
 */

/**
 * Removes duplicate entities by ID
 *
 * @param entities - Array of entities with 'id' property
 * @returns Array with unique entities (first occurrence kept)
 *
 * @example
 * const contacts = [
 *   { id: 'cnt-1', name: 'John' },
 *   { id: 'cnt-1', name: 'John' }, // duplicate
 *   { id: 'cnt-2', name: 'Jane' }
 * ];
 * uniqueById(contacts); // [{ id: 'cnt-1', name: 'John' }, { id: 'cnt-2', name: 'Jane' }]
 */
export function uniqueById<T extends { id: string }>(entities: T[]): T[] {
  const seen = new Set<string>();
  return entities.filter((entity) => {
    if (!entity.id || seen.has(entity.id)) {
      return false;
    }
    seen.add(entity.id);
    return true;
  });
}

/**
 * Groups entities by a key function
 *
 * @param entities - Array of entities
 * @param keyFn - Function to extract grouping key
 * @returns Map of key -> entities
 *
 * @example
 * const tasks = [
 *   { id: 'tsk-1', projectId: 'prj-1' },
 *   { id: 'tsk-2', projectId: 'prj-1' },
 *   { id: 'tsk-3', projectId: 'prj-2' }
 * ];
 * groupBy(tasks, t => t.projectId);
 * // Map { 'prj-1' => [...], 'prj-2' => [...] }
 */
export function groupBy<T, K>(
  entities: T[],
  keyFn: (entity: T) => K
): Map<K, T[]> {
  const groups = new Map<K, T[]>();
  for (const entity of entities) {
    const key = keyFn(entity);
    const group = groups.get(key) || [];
    group.push(entity);
    groups.set(key, group);
  }
  return groups;
}
